import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FixedSizeList as List } from 'react-window';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { formatName, formatPhone, validatePhone } from '../utils/formatting';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';
import { 
  Search, 
  Plus, 
  User, 
  Phone,
  Trash2,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Edit,
  AlertCircle
} from 'lucide-react';
import { getPatients, createPatient, deletePatient, updatePatient, getPatient, savePatientSignature } from '../services/api';
import { toast } from 'sonner';
import { DatePicker } from '../components/ui/date-picker';
import { PatientEditDialog } from '../components/patient/PatientEditDialog';
import { SignatureCapture } from '../components/patient/SignatureCapture';

// CNP validation function
const validateCNP = (cnp) => {
  if (!cnp || cnp.length !== 13) return { valid: false, error: 'CNP-ul trebuie să aibă 13 cifre' };
  if (!/^\d{13}$/.test(cnp)) return { valid: false, error: 'CNP-ul trebuie să conțină doar cifre' };
  
  const firstDigit = parseInt(cnp[0]);
  if (![1, 2, 3, 4, 5, 6, 7, 8].includes(firstDigit)) {
    return { valid: false, error: 'Prima cifră a CNP-ului este invalidă' };
  }
  
  const month = parseInt(cnp.substring(3, 5));
  if (month < 1 || month > 12) {
    return { valid: false, error: 'Luna din CNP este invalidă' };
  }
  
  const day = parseInt(cnp.substring(5, 7));
  if (day < 1 || day > 31) {
    return { valid: false, error: 'Ziua din CNP este invalidă' };
  }
  
  // Control digit validation
  const controlKey = '279146358279';
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnp[i]) * parseInt(controlKey[i]);
  }
  let checkDigit = sum % 11;
  if (checkDigit === 10) checkDigit = 1;
  
  if (parseInt(cnp[12]) !== checkDigit) {
    return { valid: false, error: 'Cifra de control a CNP-ului este invalidă' };
  }
  
  return { valid: true, error: null };
};

// Parse CNP to extract date and gender
const parseCNP = (cnp) => {
  if (!cnp || cnp.length < 7) return null;
  
  const firstDigit = parseInt(cnp[0]);
  const yearPart = cnp.substring(1, 3);
  const month = cnp.substring(3, 5);
  const day = cnp.substring(5, 7);
  
  let century;
  let gender;
  
  switch (firstDigit) {
    case 1: case 2:
      century = '19';
      gender = firstDigit === 1 ? 'M' : 'F';
      break;
    case 3: case 4:
      century = '18';
      gender = firstDigit === 3 ? 'M' : 'F';
      break;
    case 5: case 6:
      century = '20';
      gender = firstDigit === 5 ? 'M' : 'F';
      break;
    case 7: case 8:
      century = '20';
      gender = firstDigit === 7 ? 'M' : 'F';
      break;
    default:
      return null;
  }
  
  const year = century + yearPart;
  const dateOfBirth = `${year}-${month}-${day}`;
  
  const date = new Date(dateOfBirth);
  if (isNaN(date.getTime())) return null;
  
  return { dateOfBirth, gender };
};

const PAGE_SIZE = 50;

const PatientsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [cnpError, setCnpError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emergencyPhoneError, setEmergencyPhoneError] = useState('');
  
  // Mobile edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // Mobile signature capture state
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signaturePatient, setSignaturePatient] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  
  const listContainerRef = useRef(null);
  const [listHeight, setListHeight] = useState(500);
  
  const [formData, setFormData] = useState({
    cnp: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: 'M',
    height: '',
    phone: '',
    email: '',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    blood_type: '',
    allergies: '',
    notes: '',
    diagnoses: []
  });

  // Detect mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      // Update list height based on container, accounting for pagination space
      if (listContainerRef.current) {
        const containerHeight = listContainerRef.current.clientHeight;
        // Subtract 64px for pagination controls (padding + height)
        setListHeight(Math.max(containerHeight - 64, 300));
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchPatients = useCallback(async (search = '', page = 1) => {
    try {
      setLoading(true);
      const response = await getPatients(search, page, PAGE_SIZE);
      const { patients: patientList, total, total_pages } = response.data;
      
      setPatients(patientList);
      setTotalPatients(total);
      setTotalPages(total_pages);
      setCurrentPage(page);
    } catch (error) {
      toast.error('Eroare la încărcarea pacienților');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, []);

  // Recalculate height when patients change
  useEffect(() => {
    if (listContainerRef.current) {
      const containerHeight = listContainerRef.current.clientHeight;
      setListHeight(Math.max(containerHeight - 64, 300));
    }
  }, [patients]);

  // Debounced search
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchPatients(searchTerm, 1);
    }, 300);
    return () => clearTimeout(delaySearch);
  }, [searchTerm, fetchPatients]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchPatients(searchTerm, newPage);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cnpValidation = validateCNP(formData.cnp);
    if (!cnpValidation.valid) {
      setCnpError(cnpValidation.error);
      toast.error(cnpValidation.error);
      return;
    }

    // Validate phone
    if (formData.phone) {
      const phoneValidation = validatePhone(formData.phone);
      if (!phoneValidation.valid) {
        setPhoneError(phoneValidation.error);
        toast.error(phoneValidation.error);
        return;
      }
    }

    // Validate emergency phone
    if (formData.emergency_phone) {
      const emergencyPhoneValidation = validatePhone(formData.emergency_phone);
      if (!emergencyPhoneValidation.valid) {
        setEmergencyPhoneError(emergencyPhoneValidation.error);
        toast.error(emergencyPhoneValidation.error);
        return;
      }
    }
    
    try {
      const cleanedData = {
        ...formData,
        height: formData.height === '' ? null : parseFloat(formData.height),
        phone: formData.phone || null,
        email: formData.email || null,
        address: formData.address || null,
        emergency_contact: formData.emergency_contact || null,
        emergency_phone: formData.emergency_phone || null,
        blood_type: formData.blood_type || null,
        allergies: formData.allergies || null,
        notes: formData.notes || null,
      };
      
      await createPatient(cleanedData);
      toast.success('Pacient adăugat cu succes!');
      setDialogOpen(false);
      resetForm();
      fetchPatients(searchTerm, 1);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Eroare la adăugarea pacientului';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail.map(err => err.msg || err.message).join(', ');
      }
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm('Sigur doriți să ștergeți acest pacient?')) {
      try {
        await deletePatient(id);
        toast.success('Pacient șters cu succes!');
        fetchPatients(searchTerm, currentPage);
      } catch (error) {
        toast.error('Eroare la ștergerea pacientului');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      cnp: '',
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: 'M',
      height: '',
      phone: '',
      email: '',
      address: '',
      emergency_contact: '',
      emergency_phone: '',
      blood_type: '',
      allergies: '',
      notes: '',
      diagnoses: []
    });
    setCnpError('');
    setPhoneError('');
    setEmergencyPhoneError('');
  };

  const handlePhoneChange = (value) => {
    const formatted = formatPhone(value);
    setFormData(prev => ({ ...prev, phone: formatted }));
    
    if (formatted.length > 0) {
      const validation = validatePhone(formatted);
      setPhoneError(validation.error || '');
    } else {
      setPhoneError('');
    }
  };

  const handleEmergencyPhoneChange = (value) => {
    const formatted = formatPhone(value);
    setFormData(prev => ({ ...prev, emergency_phone: formatted }));
    
    if (formatted.length > 0) {
      const validation = validatePhone(formatted);
      setEmergencyPhoneError(validation.error || '');
    } else {
      setEmergencyPhoneError('');
    }
  };

  const handleCNPChange = (e) => {
    const cnp = e.target.value.replace(/\D/g, '').slice(0, 13);
    setFormData(prev => ({ ...prev, cnp }));
    setCnpError('');
    
    if (cnp.length === 13) {
      const parsed = parseCNP(cnp);
      if (parsed) {
        setFormData(prev => ({
          ...prev,
          cnp,
          date_of_birth: parsed.dateOfBirth,
          gender: parsed.gender
        }));
      }
    }
  };

  const canDelete = user?.role === 'admin';

  // Open edit dialog for mobile
  const openMobileEditDialog = async (patient) => {
    try {
      const response = await getPatient(patient.id);
      setSelectedPatient(response.data);
      setEditDialogOpen(true);
    } catch (error) {
      toast.error('Eroare la încărcarea datelor pacientului');
    }
  };

  // Handle patient update from edit dialog
  const handlePatientUpdate = async (patientId, data) => {
    try {
      await updatePatient(patientId, data);
      toast.success('Pacient actualizat cu succes!');
      setEditDialogOpen(false);
      setSelectedPatient(null);
      fetchPatients(searchTerm, currentPage);
    } catch (error) {
      toast.error('Eroare la actualizarea pacientului');
    }
  };

  // Placeholder patient IDs
  const PLACEHOLDER_PATIENT_ID = "00000000-0000-0000-0000-000000000001";
  const ADMIN_PLACEHOLDER_ID = "00000000-0000-0000-0000-000000000002";

  // Handle signature save
  const handleSignatureSave = async (signatureData) => {
    if (!signaturePatient) return;
    try {
      await savePatientSignature(signaturePatient.id, signatureData);
      toast.success('Semnătura a fost salvată!');
      setSignatureOpen(false);
      setSignaturePatient(null);
      fetchPatients(searchTerm, currentPage);
    } catch (error) {
      toast.error('Eroare la salvarea semnăturii');
    }
  };

  // Virtualized row renderer
  const PatientRow = ({ index, style }) => {
    const patient = patients[index];
    if (!patient) return null;

    const isPlaceholder = patient.is_placeholder || patient.id === PLACEHOLDER_PATIENT_ID || patient.id === ADMIN_PLACEHOLDER_ID;
    const isAdminPlaceholder = patient.placeholder_type === 'admin' || patient.id === ADMIN_PLACEHOLDER_ID;

    // Color scheme based on placeholder type
    const getPlaceholderColors = () => {
      if (isAdminPlaceholder) {
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          iconBg: 'bg-red-200',
          iconText: 'text-red-700',
          text: 'text-red-800',
          subtext: 'text-red-600',
          badge: 'bg-red-200 text-red-800'
        };
      }
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-300',
        iconBg: 'bg-amber-200',
        iconText: 'text-amber-700',
        text: 'text-amber-800',
        subtext: 'text-amber-600',
        badge: 'bg-amber-200 text-amber-800'
      };
    };

    const colors = getPlaceholderColors();

    return (
      <div 
        style={style}
        className="px-2"
      >
        <div
          className={cn(
            "flex items-center justify-between p-4 rounded-lg border transition-colors h-[72px]",
            isPlaceholder 
              ? `${colors.bg} ${colors.border} cursor-default` 
              : "bg-white border-slate-200 hover:bg-slate-50 cursor-pointer"
          )}
          onClick={() => !isPlaceholder && navigate(`/patients/${patient.id}`)}
          data-testid={`patient-row-${patient.id}`}
        >
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
              isPlaceholder ? colors.iconBg : theme.primaryBgLight
            )}>
              <User className={cn("w-5 h-5", isPlaceholder ? colors.iconText : theme.primaryText)} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className={cn(
                  "font-semibold truncate",
                  isPlaceholder ? colors.text : "text-slate-900"
                )}>
                  {patient.last_name} {patient.first_name}
                </p>
                {isPlaceholder && (
                  <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", colors.badge)}>
                    {isAdminPlaceholder ? 'Admin' : 'Programări rapide'}
                  </span>
                )}
              </div>
              <p className={cn(
                "text-sm truncate",
                isPlaceholder ? `${colors.subtext} italic` : "text-slate-500"
              )}>
                {isPlaceholder 
                  ? (isAdminPlaceholder ? "Pentru activități administrative" : "Pentru programări fără date complete") 
                  : `CNP: ${patient.cnp}`}
              </p>
            </div>
          </div>
          
          {!isPlaceholder && (
            <div className="hidden md:flex items-center gap-6 flex-shrink-0">
              <div className="flex items-center gap-2 text-slate-600">
                <Phone className="w-4 h-4" />
                <span className="text-sm">{patient.phone || '-'}</span>
              </div>
            </div>
          )}
          
          {canDelete && !isPlaceholder && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleDelete(patient.id, e)}
              className="ml-4 text-slate-400 hover:text-red-500 flex-shrink-0"
              data-testid={`delete-patient-${patient.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Mobile row renderer
  const MobilePatientRow = ({ index, style }) => {
    const patient = patients[index];
    if (!patient) return null;

    const isPlaceholder = patient.is_placeholder || patient.id === PLACEHOLDER_PATIENT_ID || patient.id === ADMIN_PLACEHOLDER_ID;
    const isAdminPlaceholder = patient.placeholder_type === 'admin' || patient.id === ADMIN_PLACEHOLDER_ID;

    // Color scheme based on placeholder type
    const getPlaceholderColors = () => {
      if (isAdminPlaceholder) {
        return {
          bg: 'bg-red-50',
          border: 'border-red-300',
          iconBg: 'bg-red-200',
          iconText: 'text-red-700',
          text: 'text-red-800',
          subtext: 'text-red-600',
          badge: 'bg-red-200 text-red-800'
        };
      }
      return {
        bg: 'bg-amber-50',
        border: 'border-amber-300',
        iconBg: 'bg-amber-200',
        iconText: 'text-amber-700',
        text: 'text-amber-800',
        subtext: 'text-amber-600',
        badge: 'bg-amber-200 text-amber-800'
      };
    };

    const colors = getPlaceholderColors();

    return (
      <div 
        style={style}
        className="px-2"
      >
        <div
          className={cn(
            "flex flex-col p-3 rounded-lg border",
            isPlaceholder 
              ? `${colors.bg} ${colors.border} cursor-default h-[72px] justify-center` 
              : "bg-white border-slate-200 h-[84px] justify-between"
          )}
          data-testid={`patient-row-${patient.id}`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0",
              isPlaceholder ? colors.iconBg : theme.primaryBgLight
            )}>
              <User className={cn("w-4 h-4", isPlaceholder ? colors.iconText : theme.primaryText)} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className={cn(
                  "font-medium text-sm",
                  isPlaceholder ? colors.text : "text-slate-900"
                )}>
                  {patient.last_name} {patient.first_name}
                </p>
                {isPlaceholder && (
                  <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap", colors.badge)}>
                    {isAdminPlaceholder ? 'Admin' : 'Rapid'}
                  </span>
                )}
              </div>
              {isPlaceholder && (
                <p className={cn("text-xs italic truncate", colors.subtext)}>
                  {isAdminPlaceholder ? "Activități admin" : "Programări fără date"}
                </p>
              )}
            </div>
          </div>
          
          {!isPlaceholder && (
            <div className="flex items-center gap-2 ml-12">
              <Button
                variant="outline"
                size="sm"
                className="px-3 py-1.5 h-8 text-slate-600 border-slate-300"
                onClick={(e) => {
                  e.stopPropagation();
                  openMobileEditDialog(patient);
                }}
                data-testid={`mobile-edit-${patient.id}`}
              >
                <Edit className="w-3.5 h-3.5 mr-1.5" />
                <span className="text-xs">Editează</span>
              </Button>
              {!patient.signature && (
                <Button
                  variant="outline"
                  size="sm"
                  className="px-3 py-1.5 h-8 text-red-600 border-red-300 hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSignaturePatient(patient);
                    setSignatureOpen(true);
                  }}
                  data-testid={`mobile-signature-${patient.id}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                  <span className="text-xs">Semnează</span>
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => handleDelete(patient.id, e)}
                  className="text-slate-400 hover:text-red-500 p-1 h-auto"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col relative" data-testid="patients-page">
      {/* Sticky Header */}
      <div className="flex-shrink-0 bg-white pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Pacienți</h1>
            <p className="text-sm text-slate-500">
              {totalPatients} pacienți înregistrați
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className={cn(theme.primaryBg, theme.primaryBgHover)}
                data-testid="add-patient-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Adaugă Pacient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Adaugă Pacient Nou</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Nume *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: formatName(e.target.value) })}
                      required
                      placeholder="ex: Popescu sau Ionescu-Popa"
                      data-testid="patient-last-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="first_name">Prenume *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: formatName(e.target.value) })}
                      required
                      placeholder="ex: Ion sau Ana-Maria"
                      data-testid="patient-first-name-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cnp">CNP *</Label>
                    <Input
                      id="cnp"
                      value={formData.cnp}
                      onChange={handleCNPChange}
                      placeholder="1234567890123"
                      maxLength={13}
                      required
                      className={cnpError ? 'border-red-500' : ''}
                      data-testid="patient-cnp-input"
                    />
                    {cnpError && (
                      <p className="text-xs text-red-500">{cnpError}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Data nașterii *</Label>
                    <DatePicker
                      value={formData.date_of_birth}
                      onChange={(date) => setFormData({ ...formData, date_of_birth: date })}
                      placeholder="Se completează din CNP"
                      disabled={true}
                      data-testid="patient-dob-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gen *</Label>
                    <Select
                      value={formData.gender}
                      onValueChange={(value) => setFormData({ ...formData, gender: value })}
                      disabled
                    >
                      <SelectTrigger data-testid="patient-gender-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="M">Masculin</SelectItem>
                        <SelectItem value="F">Feminin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="height">Înălțime (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      placeholder="170"
                      data-testid="patient-height-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      placeholder="07xxxxxxxx"
                      maxLength={10}
                      className={phoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      data-testid="patient-phone-input"
                    />
                    {phoneError && (
                      <div className="flex items-center gap-1.5 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{phoneError}</span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@example.com"
                      data-testid="patient-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Adresă</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Strada, număr, oraș"
                    data-testid="patient-address-input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact">Contact de urgență</Label>
                    <Input
                      id="emergency_contact"
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                      placeholder="Nume persoană"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_phone">Telefon urgență</Label>
                    <Input
                      id="emergency_phone"
                      value={formData.emergency_phone}
                      onChange={(e) => handleEmergencyPhoneChange(e.target.value)}
                      placeholder="07xxxxxxxx"
                      maxLength={10}
                      className={emergencyPhoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                    />
                    {emergencyPhoneError && (
                      <div className="flex items-center gap-1.5 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{emergencyPhoneError}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Grupa sanguină</Label>
                    <Select
                      value={formData.blood_type}
                      onValueChange={(value) => setFormData({ ...formData, blood_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selectează" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A+">A+</SelectItem>
                        <SelectItem value="A-">A-</SelectItem>
                        <SelectItem value="B+">B+</SelectItem>
                        <SelectItem value="B-">B-</SelectItem>
                        <SelectItem value="AB+">AB+</SelectItem>
                        <SelectItem value="AB-">AB-</SelectItem>
                        <SelectItem value="O+">O+</SelectItem>
                        <SelectItem value="O-">O-</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="allergies">Alergii</Label>
                    <Input
                      id="allergies"
                      value={formData.allergies}
                      onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                      placeholder="Separate prin virgulă"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Note</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Observații suplimentare"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Anulează
                  </Button>
                  <Button type="submit" className={cn(theme.primaryBg, theme.primaryBgHover)}>
                    Salvează
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Caută după nume sau CNP..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="patient-search-input"
          />
        </div>
      </div>

      {/* Virtualized Patient List */}
      <div 
        ref={listContainerRef}
        className="flex-1 min-h-0 pb-16"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : patients.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500">
            <User className="w-12 h-12 mb-4 text-slate-300" />
            <p>Nu s-au găsit pacienți</p>
          </div>
        ) : (
          <List
            height={listHeight || 500}
            itemCount={patients.length}
            itemSize={isMobile ? 92 : 80}
            width="100%"
            className="scrollbar-thin"
          >
            {isMobile ? MobilePatientRow : PatientRow}
          </List>
        )}
      </div>

      {/* Pagination Controls - Fixed at bottom */}
      {totalPages > 1 && (
        <div className="absolute bottom-0 left-0 right-0 py-3 px-2 border-t border-slate-200 bg-white z-20 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Pagina {currentPage} din {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      className={cn(
                        "w-8 h-8 p-0",
                        pageNum === currentPage && cn(theme.primaryBg, theme.primaryBgHover)
                      )}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Edit Dialog */}
      {selectedPatient && (
        <PatientEditDialog
          open={editDialogOpen}
          onOpenChange={(open) => {
            setEditDialogOpen(open);
            if (!open) setSelectedPatient(null);
          }}
          patient={selectedPatient}
          onSave={handlePatientUpdate}
        />
      )}

      {/* Mobile Signature Capture */}
      <SignatureCapture
        open={signatureOpen}
        onClose={() => {
          setSignatureOpen(false);
          setSignaturePatient(null);
        }}
        onSave={handleSignatureSave}
        patientName={signaturePatient ? `${signaturePatient.last_name} ${signaturePatient.first_name}` : ''}
      />
    </div>
  );
};

export default PatientsPage;
